// ============================================================================
// SCHOOL GLYPHS — transcribed path-for-path from LifeOS School.dc.html (6a/6b/6c).
//
// A second icon module, not an extension of components/icons.tsx, for one boring reason: three
// screens are being ported in parallel right now and a shared file edited by all three merges
// badly. These are the glyphs only School uses; anything a second screen needs moves up to
// components/icons.tsx on its second use (the Rule of Three, blueprint/02).
//
// Same contract as the main set: viewBox 0 0 24 24, fill none, stroke="currentColor" so the
// colour comes from a token class, round caps and joins. A raw hex in here would fail the ship
// gate's brand audit, which is the point.
// ============================================================================

import type { IconProps } from '../icons'

function Glyph({ size = 16, strokeWidth = 1.7, className, d }: IconProps & { d: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path d={d} />
    </svg>
  )
}

/** Breadcrumb back-arrow (6b/6c header band). */
export const ChevronLeft = (p: IconProps) => <Glyph strokeWidth={2} {...p} d="M14 6l-6 6 6 6" />

/** Lecture row expand/collapse. Rotate 180° for the open state, as the mockup does. */
export const ChevronDown = (p: IconProps) => <Glyph strokeWidth={2} {...p} d="M6 9.5l6 6 6-6" />

/** Announcements — a speech bubble with two rules (6a) or none (6b). */
export const Speech = (p: IconProps) => (
  <Glyph {...p} d="M4 6.4A2.4 2.4 0 0 1 6.4 4h11.2A2.4 2.4 0 0 1 20 6.4v7.2a2.4 2.4 0 0 1-2.4 2.4H9l-5 4zM8.4 10h7.2" />
)

/** The same bubble with no rules — the course page's announcements, and the ask bar. */
export const SpeechPlain = (p: IconProps) => (
  <Glyph {...p} d="M4 6.4A2.4 2.4 0 0 1 6.4 4h11.2A2.4 2.4 0 0 1 20 6.4v7.2a2.4 2.4 0 0 1-2.4 2.4H9l-5 4z" />
)

/** A document. Assignments header, the syllabus panel, and the 'Syllabus PDF' meta link. */
export const Doc = (p: IconProps) => <Glyph {...p} d="M6 3.6h8.4L19 8.2v12.2H6zM14 3.6v5h5" />

/** A document with text — the 'Full transcript' button. */
export const DocLines = (p: IconProps) => (
  <Glyph {...p} d="M6 3.6h8.4L19 8.2v12.2H6zM9 12h7M9 16h5" />
)

/** A document with more text — the assignments card header. */
export const DocText = (p: IconProps) => (
  <Glyph {...p} d="M6 3.6h8.4L19 8.2v12.2H6zM14 3.6v5h5M9 13h6M9 16.6h4" />
)

/** Lectures. */
export const Mic = (p: IconProps) => (
  <Glyph {...p} d="M12 4.6a2.4 2.4 0 0 1 2.4 2.4v4.4a2.4 2.4 0 0 1-4.8 0V7A2.4 2.4 0 0 1 12 4.6M6.6 11.4a5.4 5.4 0 0 0 10.8 0M12 16.8V20" />
)

/** The hidden-grade state. A closed padlock, and it is closed on purpose. */
export const Lock = (p: IconProps) => (
  <Glyph strokeWidth={1.8} {...p} d="M6.5 10.5h11a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1M8.6 10.5V7.6a3.4 3.4 0 0 1 6.8 0" />
)

/** The syllabus provenance chip's warning-toned circle. */
export const InfoCircle = (p: IconProps) => (
  <Glyph strokeWidth={2} {...p} d="M12 3.8a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4M12 8.4h.01M12 11.6v4.4" />
)

/** Practice questions — a circled question mark. Two subpaths, so it takes its own <svg>. */
export const QuestionCircle = ({ size = 13, strokeWidth = 1.9, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <path d="M9.6 9a2.5 2.5 0 1 1 3.6 2.3c-.8.5-1.2 1-1.2 1.9M12 17.2h.01" />
    <path d="M12 3.8a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4" />
  </svg>
)

/** The audio banner's circled play glyph. Also two subpaths. */
export const PlayCircle = ({ size = 12, strokeWidth = 2, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <path d="M9.6 8.4 17 12l-7.4 3.6z" />
    <path d="M12 3.8a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4" />
  </svg>
)

/** 'audio on desktop' — a monitor, because that is literally where the file is. */
export const Monitor = (p: IconProps) => (
  <Glyph strokeWidth={2} {...p} d="M4 5.6A1.6 1.6 0 0 1 5.6 4h12.8A1.6 1.6 0 0 1 20 5.6v8.8H4zM8.4 19.4h7.2" />
)

/** Flashcards — two stacked cards. */
/** A tray with an arrow into it — Download transcript. */
export const Download = (p: IconProps) => (
  <Glyph strokeWidth={1.9} {...p} d="M12 4v10.5M7.5 10.5L12 15l4.5-4.5M5 18.5h14" />
)

export const Cards = (p: IconProps) => (
  <Glyph strokeWidth={1.9} {...p} d="M4 7.4A2.4 2.4 0 0 1 6.4 5h8.2A2.4 2.4 0 0 1 17 7.4v9.2A2.4 2.4 0 0 1 14.6 19H6.4A2.4 2.4 0 0 1 4 16.6zM20 8v9" />
)

/** The flashcard's 'flip' affordance. */
export const Flip = (p: IconProps) => (
  <Glyph strokeWidth={2.2} {...p} d="M4 12a8 8 0 0 1 13.7-5.6M20 12a8 8 0 0 1-13.7 5.6M17.7 3.6v2.8h-2.8M6.3 20.4v-2.8h2.8" />
)

/** 'I got it'. */
export const Tick = (p: IconProps) => <Glyph strokeWidth={2.4} {...p} d="M5 12.8l4.2 4.2L19 7.4" />

/** 'Missed it'. */
export const Cross = (p: IconProps) => (
  <Glyph strokeWidth={2.4} {...p} d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
)

/** The overview's 'Week view' button and the deadline runway. */
export const CalendarPlain = (p: IconProps) => (
  <Glyph strokeWidth={1.8} {...p} d="M4 7.4A2.4 2.4 0 0 1 6.4 5h11.2A2.4 2.4 0 0 1 20 7.4v11.2A2.4 2.4 0 0 1 17.6 21H6.4A2.4 2.4 0 0 1 4 18.6zM4 10h16" />
)
